import { formatExperienceText } from "../../../ey/pptx/helpers";
import { ContactInformationItem, CustomType, ProfileExportFormatted, TextItem } from "../../../types";
import { mainSectionCustomFieldNames } from "../../constants";
import { FullExperience, getPositionsByCategory } from "../../use_positions_by_category";
import {
  categoryOptions,
  categoryPositionOptions,
  contactOptions,
  header2Options,
  header3Options,
  textOptions
} from "./options";

export const EMPTY_LINE: TextItem = {
  text: "_",
  options: {
    transparency: 100,
    //actual height of space
    fontSize: 5,
    breakLine: true
  }
};

export const createContacts = (
  contactInformationItems: ContactInformationItem[],
  email: string,
  t: (text: string) => string,
  translationKey: string
) => [
  EMPTY_LINE,
  ...contactInformationItems
    .filter(({ type }) => type.includes("phone"))
    .map(({ text }) => [
      {
        text: `${t(`${translationKey}.phonePrefix`)}: `,
        options: { ...textOptions, breakLine: false, bold: false }
      },
      {
        text,
        options: { ...textOptions, breakLine: true, bold: false }
      }
    ])
    .flat(),
  { text: `${t(`${translationKey}.emailPrefix`)}: `, options: { ...textOptions, breakLine: false } },
  { text: email, options: contactOptions }
];

export const getSidebarCustomTypes = (
  order: string[],
  hidden: Record<string, boolean>,
  customTypes: CustomType[]
) =>
  order
    .filter((name) => !hidden[name] && !mainSectionCustomFieldNames.includes(name))
    .map((name) => customTypes.find(({ type }) => type.name === name))
    .filter(Boolean) as CustomType[];

export const createCustomField = (customType: CustomType) => {
  const values = customType.text ? customType.text.split("\r ") : [];

  if (values.length === 0) {
    return [];
  }

  return [
    EMPTY_LINE,
    {
      text: customType.type.display_as,
      options: header2Options
    },
    ...values.map((item) => ({
      text: item,
      options: {
        ...header3Options,
        bullet: { indent: 9 },
        bold: false
      }
    }))
  ];
};

export const createSummary = (summary: string, title: string): TextItem[] => {
  const summaryArray = summary.replace(/\r/g, "").split(/\n/);
  return [
    {
      text: title,
      options: header2Options
    },
    ...summaryArray.map((text) => ({
      text,
      options: textOptions
    }))
  ];
};

const createInternalExperiences = (
  internalExperiences: ProfileExportFormatted["_originalData"]["internal_experiences"],
  formatDate: (text: string) => string
) =>
  internalExperiences
    .map(({ description, name, metadata: { date_range } }) => [
      {
        text: `- ${formatExperienceText(formatDate, name, date_range?.start_date, date_range?.end_date)}`,
        options: { ...textOptions, bold: true, bullet: { indent: 9, code: "0020" } }
      },
      { text: description, options: { ...textOptions, bullet: { indent: 18, code: "0020" } } }
    ])
    .flat();

const getCategoryExperiences = (
  positions: FullExperience[],
  formatDate: (date: string) => string
): TextItem[] =>
  positions
    .map(({ internalExperiences, jobTitle, companyName, summary, startDate, endDate }) => {
      const ret: TextItem[] = [
        {
          text: `${companyName} - ${formatExperienceText(formatDate, jobTitle, startDate, endDate)}`,
          options: { ...categoryPositionOptions, bullet: { indent: 9 } }
        }
      ];

      if (summary) {
        ret.push({
          text: summary,
          //@ts-ignore
          options: { ...textOptions, bullet: { indent: 9, code: "0020" } }
        });
      }
      if (internalExperiences) {
        ret.push(...createInternalExperiences(internalExperiences, formatDate));
      }
      return ret;
    })
    .flat();

export const createPositions = (
  positions: ProfileExportFormatted["positions"],
  internalExperiences: ProfileExportFormatted["_originalData"]["internal_experiences"] = [],
  title: string,
  formatDate: (date: string) => string,
  t: (text: string) => string
): TextItem[] => {
  const positionsByCategory = getPositionsByCategory(positions, internalExperiences, t);

  return [
    {
      text: title,
      options: header2Options
    },
    ...positionsByCategory
      .map(([category, fullExperience]) => {
        const cat = { text: category.text, options: categoryOptions };
        return [cat, ...getCategoryExperiences(fullExperience, formatDate)];
      })
      .flat()
  ];
};
